/**
 * Who is signed in, and what the UI should therefore offer them.
 *
 * ## This file is not a security boundary
 *
 * `useCan` and `<RequireRole>` exist so a person is not shown a button that
 * would fail, and so a client never sees a nav link to a screen that would
 * 403. That is *cosmetic*. Anyone can edit the JavaScript in their browser.
 * The API calls `authorize(actor, action, kind)` server-side on every route
 * and that is the only thing standing between a caller and a row. If a rule
 * matters, it has to exist in `apps/api`, not here.
 *
 * Both sides read the same table in `packages/shared/src/policy.ts`, so the
 * UI and the API cannot disagree about what a role may do - only about
 * whether anyone checked.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, use, useCallback, useEffect, useMemo, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import type { Action, Actor, ResourceKind, Role, UserDto } from '@botguide/shared';
import { can } from '@botguide/shared';

import { ApiRequestError, authApi, setUnauthorizedHandler } from './api';
import { ROUTES } from './routes';
import { queryKeys } from './queries';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface SessionValue {
  /** The policy actor, or null when nobody is signed in. */
  actor: Actor | null;
  user: UserDto | null;
  status: SessionStatus;
  /** When the cookie expires, as an ISO instant. Null when anonymous. */
  expiresAt: string | null;
  /** Non-null only when `status` is `'error'`. */
  error: unknown;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/** A `UserDto` carries more than authorization needs. Narrow it deliberately. */
function actorFrom(user: UserDto): Actor {
  return { id: user.id, role: user.role, clientId: user.clientId };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const session = useQuery({
    queryKey: queryKeys.session,
    queryFn: authApi.session,
    /**
     * A 401 here means "not signed in", which is an answer, not a failure.
     * `authApi.session` passes `on401: 'ignore'`, so it lands as an error we
     * classify below rather than as a redirect loop on the sign-in page.
     */
    retry: (failureCount, error) => {
      if (error instanceof ApiRequestError) return false;
      return failureCount < 2;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  /**
   * Throwing away a dead session, used by both the 401 handler and sign-out.
   *
   * `removeQueries` for everything else: those answers were fetched for a
   * person who is no longer here, and the next person to sign in on this
   * browser must not see them flash up.
   *
   * `resetQueries` - NOT `clear()` - for the session itself. `clear()` empties
   * the cache but leaves a mounted observer holding its last result, so the
   * shell carries on rendering the signed-out person's name until something
   * else happens to re-render. `resetQueries` refetches the queries that are
   * actually mounted, which is the whole point.
   */
  const discardSession = useCallback(async () => {
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
    await queryClient.resetQueries({ queryKey: queryKeys.session });
  }, [queryClient]);

  /**
   * A 401 on any other call means the cookie expired mid-session. Reset and
   * let the route guards navigate - they redirect with the current location
   * attached, so the person lands back where they were after signing in.
   *
   * The ref stops a screen with six in-flight queries from running this six
   * times; the first one is the one that matters.
   */
  const discarding = useRef(false);

  useEffect(
    () =>
      setUnauthorizedHandler(() => {
        if (discarding.current) return;
        discarding.current = true;
        void discardSession().finally(() => {
          discarding.current = false;
        });
      }),
    [discardSession],
  );

  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } finally {
      // Whether or not the server acknowledged, this browser is done with it.
      await discardSession();
    }
  }, [discardSession]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
  }, [queryClient]);

  const value = useMemo<SessionValue>(() => {
    const unauthenticated = session.error instanceof ApiRequestError && session.error.isUnauthenticated;

    let status: SessionStatus;
    if (session.isPending) status = 'loading';
    else if (session.data) status = 'authenticated';
    else if (unauthenticated || !session.error) status = 'anonymous';
    else status = 'error';

    const user = session.data?.user ?? null;

    return {
      actor: user ? actorFrom(user) : null,
      user,
      status,
      expiresAt: session.data?.expiresAt ?? null,
      error: status === 'error' ? session.error : null,
      signOut,
      refresh,
    };
  }, [session.data, session.error, session.isPending, signOut, refresh]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionValue {
  const value = use(SessionContext);
  if (!value) throw new Error('useSession must be used inside a <SessionProvider>.');
  return value;
}

/**
 * Should this control be rendered? Cosmetic only - see the file header.
 *
 * Reads the same `RULES` table the API enforces, so the answer here and the
 * answer there are derived from one definition.
 */
export function useCan(action: Action, kind: ResourceKind): boolean {
  const { actor } = useSession();
  return can(actor, action, kind);
}

/* -------------------------------------------------------------------------- */
/* Route guards                                                               */
/* -------------------------------------------------------------------------- */

/**
 * A guard redirects; it never renders a half-broken screen. A screen that
 * loads and then discovers it has no data to show is worse than one that was
 * never reachable.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <RouteSpinner />;

  if (status !== 'authenticated') {
    // `from` lets SignIn return the person to where they were headed.
    return <Navigate to={ROUTES.signIn} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
  const { status, actor } = useSession();
  const location = useLocation();

  if (status === 'loading') return <RouteSpinner />;

  if (status !== 'authenticated' || !actor) {
    return <Navigate to={ROUTES.signIn} replace state={{ from: location }} />;
  }

  if (!roles.includes(actor.role)) {
    return <Navigate to={ROUTES.forbidden} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

/**
 * The gap between "the app booted" and "we know who you are". Deliberately
 * almost nothing: a full skeleton here flashes on every navigation.
 */
function RouteSpinner() {
  return (
    <div className="grid min-h-[60dvh] place-items-center" role="status" aria-label="Loading">
      <span className="size-5 animate-[spin_0.7s_linear_infinite] rounded-full border-2 border-line border-t-accent" />
    </div>
  );
}
